import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { 
  LayoutGrid, List, Calendar, Plus, Search, Filter, Users, 
  CheckCircle2, Clock, AlertTriangle, MoreVertical, ChevronDown,
  ClipboardList, Shield, FileText, Eye, Target, Award, Lightbulb,
  AlertCircle, CheckSquare, Square, MessageSquare, Link2, Trash2,
  Edit3, X, ChevronLeft, ChevronRight, Folder, UserPlus, Settings,
  BarChart3, Play, Pause, Timer, Star, Zap, TrendingUp, GanttChart,
  Sparkles, HelpCircle, BookOpen, Video, ArrowRight, Rocket, Heart,
  Flag, CalendarDays, UserCheck, Layers, PieChart, Activity
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import { Progress } from './ui/progress';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Professional header image URL
const HEADER_IMAGE_URL = "https://images.unsplash.com/photo-1600880292203-757bb62b4baf?auto=format&fit=crop&w=1200&q=80";

// Vibrant Category Configuration with gradient colors
const CATEGORY_CONFIG = {
  hazard_remediation: { icon: AlertTriangle, color: 'bg-gradient-to-r from-red-500 to-rose-600', bgLight: 'bg-red-50', textColor: 'text-red-700', label: 'Hazard Remediation', emoji: '⚠️' },
  inspection_task: { icon: Eye, color: 'bg-gradient-to-r from-orange-500 to-amber-500', bgLight: 'bg-orange-50', textColor: 'text-orange-700', label: 'Inspection Task', emoji: '🔍' },
  training_requirement: { icon: ClipboardList, color: 'bg-gradient-to-r from-yellow-500 to-amber-400', bgLight: 'bg-yellow-50', textColor: 'text-yellow-700', label: 'Training Requirement', emoji: '📚' },
  documentation_update: { icon: FileText, color: 'bg-gradient-to-r from-green-500 to-emerald-500', bgLight: 'bg-green-50', textColor: 'text-green-700', label: 'Documentation Update', emoji: '📝' },
  compliance_task: { icon: Shield, color: 'bg-gradient-to-r from-blue-500 to-indigo-500', bgLight: 'bg-blue-50', textColor: 'text-blue-700', label: 'Compliance Task', emoji: '🛡️' },
  incident_followup: { icon: AlertCircle, color: 'bg-gradient-to-r from-purple-500 to-violet-500', bgLight: 'bg-purple-50', textColor: 'text-purple-700', label: 'Incident Follow-up', emoji: '🔄' },
  safety_inspection: { icon: Eye, color: 'bg-gradient-to-r from-orange-400 to-yellow-500', bgLight: 'bg-orange-50', textColor: 'text-orange-700', label: 'Safety Inspection', emoji: '👁️' },
  incident_response: { icon: AlertTriangle, color: 'bg-gradient-to-r from-red-600 to-pink-600', bgLight: 'bg-red-50', textColor: 'text-red-700', label: 'Incident Response', emoji: '🚨' },
  pre_task_planning: { icon: ClipboardList, color: 'bg-gradient-to-r from-cyan-500 to-blue-500', bgLight: 'bg-cyan-50', textColor: 'text-cyan-700', label: 'Pre-Task Planning', emoji: '📋' },
  job_safety_analysis: { icon: Target, color: 'bg-gradient-to-r from-indigo-500 to-purple-500', bgLight: 'bg-indigo-50', textColor: 'text-indigo-700', label: 'Job Safety Analysis', emoji: '🎯' },
  site_safety_plan: { icon: FileText, color: 'bg-gradient-to-r from-emerald-500 to-teal-500', bgLight: 'bg-emerald-50', textColor: 'text-emerald-700', label: 'Site Safety Plan', emoji: '🏗️' },
  activity_hazard_analysis: { icon: AlertCircle, color: 'bg-gradient-to-r from-amber-500 to-orange-500', bgLight: 'bg-amber-50', textColor: 'text-amber-700', label: 'Activity Hazard Analysis', emoji: '⚡' },
  safety_program: { icon: Shield, color: 'bg-gradient-to-r from-blue-600 to-cyan-600', bgLight: 'bg-blue-50', textColor: 'text-blue-700', label: 'Safety Program', emoji: '🎖️' },
  safety_procedure: { icon: FileText, color: 'bg-gradient-to-r from-teal-500 to-green-500', bgLight: 'bg-teal-50', textColor: 'text-teal-700', label: 'Safety Procedure', emoji: '📖' },
  safe_practice: { icon: CheckCircle2, color: 'bg-gradient-to-r from-green-600 to-emerald-600', bgLight: 'bg-green-50', textColor: 'text-green-700', label: 'Safe Practice', emoji: '✅' },
  observation: { icon: Eye, color: 'bg-gradient-to-r from-slate-500 to-gray-600', bgLight: 'bg-slate-50', textColor: 'text-slate-700', label: 'Observation', emoji: '👀' },
  safety_spotlight: { icon: Lightbulb, color: 'bg-gradient-to-r from-yellow-400 to-orange-400', bgLight: 'bg-yellow-50', textColor: 'text-yellow-700', label: 'Safety Spotlight', emoji: '💡' },
  standard_protocol: { icon: FileText, color: 'bg-gradient-to-r from-gray-500 to-slate-500', bgLight: 'bg-gray-50', textColor: 'text-gray-700', label: 'Standard Protocol', emoji: '📜' },
  good_catch: { icon: Award, color: 'bg-gradient-to-r from-emerald-600 to-green-500', bgLight: 'bg-emerald-50', textColor: 'text-emerald-700', label: 'Good Catch', emoji: '🏆' },
  positive_action: { icon: Award, color: 'bg-gradient-to-r from-green-500 to-lime-500', bgLight: 'bg-green-50', textColor: 'text-green-700', label: 'Positive Action', emoji: '🌟' }
};

// Enhanced Status Configuration with vibrant colors
const STATUS_CONFIG = {
  todo: { label: 'To Do', color: 'bg-slate-500', bgLight: 'bg-slate-100', textColor: 'text-slate-700', icon: Square, emoji: '📥' },
  in_progress: { label: 'In Progress', color: 'bg-blue-500', bgLight: 'bg-blue-100', textColor: 'text-blue-700', icon: Play, emoji: '🔄' },
  review: { label: 'In Review', color: 'bg-amber-500', bgLight: 'bg-amber-100', textColor: 'text-amber-700', icon: Eye, emoji: '👁️' },
  completed: { label: 'Completed', color: 'bg-green-500', bgLight: 'bg-green-100', textColor: 'text-green-700', icon: CheckCircle2, emoji: '✅' },
  blocked: { label: 'Blocked', color: 'bg-red-500', bgLight: 'bg-red-100', textColor: 'text-red-700', icon: AlertTriangle, emoji: '🚫' },
  cancelled: { label: 'Cancelled', color: 'bg-gray-400', bgLight: 'bg-gray-100', textColor: 'text-gray-600', icon: X, emoji: '❌' }
};

// Priority Configuration with visual indicators
const PRIORITY_CONFIG = {
  low: { label: 'Low', color: 'bg-slate-400', bgLight: 'bg-slate-100', textColor: 'text-slate-600', icon: Flag, emoji: '🔵' },
  medium: { label: 'Medium', color: 'bg-blue-500', bgLight: 'bg-blue-100', textColor: 'text-blue-600', icon: Flag, emoji: '🟡' },
  high: { label: 'High', color: 'bg-orange-500', bgLight: 'bg-orange-100', textColor: 'text-orange-600', icon: Flag, emoji: '🟠' },
  critical: { label: 'Critical', color: 'bg-red-500', bgLight: 'bg-red-100', textColor: 'text-red-600', icon: AlertTriangle, emoji: '🔴' },
  immediate: { label: 'Immediate', color: 'bg-red-600', bgLight: 'bg-red-200', textColor: 'text-red-700', icon: Zap, emoji: '⚡' }
};

// Role Configuration
const ROLE_CONFIG = {
  safety_manager: { label: 'Safety Manager', color: 'bg-purple-500', icon: Shield },
  safety_officer: { label: 'Safety Officer', color: 'bg-blue-500', icon: UserCheck },
  supervisor: { label: 'Supervisor', color: 'bg-green-500', icon: Users },
  team_lead: { label: 'Team Lead', color: 'bg-orange-500', icon: Target },
  worker: { label: 'Worker', color: 'bg-slate-500', icon: Users },
  inspector: { label: 'Inspector', color: 'bg-amber-500', icon: Eye },
  trainer: { label: 'Trainer', color: 'bg-cyan-500', icon: BookOpen }
};

// Tutorial Steps
const TUTORIAL_STEPS = [
  {
    title: "Welcome to Safety Work Management! 🎉",
    description: "Transform how your team manages safety tasks, procedures, and compliance workflows.",
    icon: Rocket,
    color: "from-purple-500 to-pink-500",
    features: [
      "Kanban boards for visual task management",
      "Gantt charts for timeline planning",
      "Real-time team collaboration",
      "Pre-built safety workflow templates"
    ]
  },
  {
    title: "Create Projects from Templates 📋",
    description: "Start quickly with pre-built safety workflow templates designed by experts.",
    icon: Layers,
    color: "from-blue-500 to-cyan-500",
    features: [
      "Job Safety Analysis (JSA)",
      "Incident Response Workflow",
      "Safety Inspection Checklist",
      "Pre-Task Planning"
    ]
  },
  {
    title: "Assign Tasks & Track Progress 👥",
    description: "Assign tasks to team members, set priorities, and track completion in real-time.",
    icon: UserCheck,
    color: "from-green-500 to-emerald-500",
    features: [
      "Role-based task assignment",
      "Due date tracking with reminders",
      "Progress visualization",
      "Workload balancing"
    ]
  },
  {
    title: "Visualize with Gantt Charts 📊",
    description: "Plan and track safety initiatives with powerful timeline visualization.",
    icon: GanttChart,
    color: "from-orange-500 to-red-500",
    features: [
      "Timeline view of all tasks",
      "Dependency management",
      "Milestone tracking",
      "Resource allocation"
    ]
  },
  {
    title: "Collaborate & Communicate 💬",
    description: "Keep your team aligned with built-in collaboration tools.",
    icon: MessageSquare,
    color: "from-indigo-500 to-purple-500",
    features: [
      "Task comments & mentions",
      "Activity tracking",
      "File attachments",
      "Notifications"
    ]
  }
];

// Feature Highlights for Dashboard with actions
const FEATURE_HIGHLIGHTS = [
  { icon: LayoutGrid, label: "Kanban Board", description: "Visual task management", color: "text-blue-500", bg: "bg-blue-50", gradient: "from-blue-500 to-indigo-500", action: "board" },
  { icon: GanttChart, label: "Gantt Chart", description: "Timeline planning", color: "text-purple-500", bg: "bg-purple-50", gradient: "from-purple-500 to-pink-500", action: "gantt" },
  { icon: CalendarDays, label: "Calendar View", description: "Schedule overview", color: "text-green-500", bg: "bg-green-50", gradient: "from-green-500 to-emerald-500", action: "calendar" },
  { icon: Users, label: "Team Roles", description: "Role-based assignments", color: "text-orange-500", bg: "bg-orange-50", gradient: "from-orange-500 to-amber-500", action: "teams" },
  { icon: PieChart, label: "Analytics", description: "Progress insights", color: "text-pink-500", bg: "bg-pink-50", gradient: "from-pink-500 to-rose-500", action: "analytics" },
  { icon: Sparkles, label: "Templates", description: "Quick start workflows", color: "text-amber-500", bg: "bg-amber-50", gradient: "from-amber-500 to-yellow-500", action: "templates" }
];

// Tutorial Modal Component
const TutorialModal = ({ isOpen, onClose, onComplete }) => {
  const [currentStep, setCurrentStep] = useState(0);
  
  const handleNext = () => {
    if (currentStep < TUTORIAL_STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      onComplete();
      onClose();
    }
  };
  
  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };
  
  const step = TUTORIAL_STEPS[currentStep];
  const StepIcon = step.icon;
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <div className="relative">
          {/* Skip Button */}
          <Button 
            variant="ghost" 
            size="sm"
            onClick={() => { onComplete(); onClose(); }}
            className="absolute top-0 right-0 text-gray-400 hover:text-gray-600"
          >
            Skip Tutorial
            <X className="w-4 h-4 ml-1" />
          </Button>
          
          {/* Progress Bar */}
          <div className="flex gap-1 mb-6 mt-8">
            {TUTORIAL_STEPS.map((_, idx) => (
              <div 
                key={idx} 
                className={`h-1 flex-1 rounded-full transition-all ${idx <= currentStep ? 'bg-gradient-to-r ' + step.color : 'bg-gray-200'}`}
              />
            ))}
          </div>
          
          {/* Step Content */}
          <div className="text-center mb-6">
            <div className={`w-20 h-20 mx-auto rounded-2xl bg-gradient-to-r ${step.color} flex items-center justify-center mb-4 shadow-lg`}>
              <StepIcon className="w-10 h-10 text-white" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">{step.title}</h2>
            <p className="text-gray-600">{step.description}</p>
          </div>
          
          {/* Features */}
          <div className="grid grid-cols-2 gap-3 mb-6">
            {step.features.map((feature, idx) => (
              <div key={idx} className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0" />
                <span className="text-sm text-gray-700">{feature}</span>
              </div>
            ))}
          </div>
          
          {/* Navigation */}
          <div className="flex justify-between items-center">
            <Button 
              variant="ghost" 
              onClick={handlePrev}
              disabled={currentStep === 0}
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              Back
            </Button>
            
            <span className="text-sm text-gray-500">
              {currentStep + 1} of {TUTORIAL_STEPS.length}
            </span>
            
            <Button onClick={handleNext} className={`bg-gradient-to-r ${step.color} text-white`}>
              {currentStep === TUTORIAL_STEPS.length - 1 ? (
                <>
                  Get Started
                  <Rocket className="w-4 h-4 ml-1" />
                </>
              ) : (
                <>
                  Next
                  <ChevronRight className="w-4 h-4 ml-1" />
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Enhanced Task Card with vibrant colors
const TaskCard = ({ task, onClick, onStatusChange }) => {
  const category = CATEGORY_CONFIG[task.category] || CATEGORY_CONFIG.compliance_task;
  const CategoryIcon = category.icon;
  const priority = PRIORITY_CONFIG[task.priority] || PRIORITY_CONFIG.medium;
  const status = STATUS_CONFIG[task.status] || STATUS_CONFIG.todo;
  const isOverdue = task.due_date && new Date(task.due_date) < new Date() && task.status !== 'completed';
  
  // Calculate progress from checklist
  const checklistProgress = task.checklist?.length > 0 
    ? Math.round((task.checklist.filter(c => c.completed).length / task.checklist.length) * 100)
    : 0;
  
  return (
    <div 
      className={`bg-white rounded-xl border-2 border-gray-100 p-4 mb-3 cursor-pointer hover:shadow-lg hover:border-gray-200 transition-all duration-200 group ${isOverdue ? 'border-l-4 border-l-red-500' : ''}`}
      onClick={() => onClick(task)}
      draggable
      onDragStart={(e) => e.dataTransfer.setData('taskId', task.id)}
    >
      {/* Category Badge */}
      <div className="flex items-center justify-between mb-3">
        <div className={`flex items-center gap-2 px-2 py-1 rounded-lg ${category.bgLight}`}>
          <span className="text-sm">{category.emoji}</span>
          <span className={`text-xs font-medium ${category.textColor}`}>{category.label}</span>
        </div>
        <div className={`px-2 py-1 rounded-lg ${priority.bgLight}`}>
          <span className="text-xs">{priority.emoji}</span>
        </div>
      </div>
      
      {/* Title */}
      <h4 className="font-semibold text-gray-900 mb-2 line-clamp-2 group-hover:text-blue-600 transition-colors">
        {task.title}
      </h4>
      
      {/* Description */}
      {task.description && (
        <p className="text-sm text-gray-500 mb-3 line-clamp-2">{task.description}</p>
      )}
      
      {/* Progress Bar */}
      {task.checklist?.length > 0 && (
        <div className="mb-3">
          <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
            <span>Progress</span>
            <span>{checklistProgress}%</span>
          </div>
          <Progress value={checklistProgress} className="h-2" />
        </div>
      )}
      
      {/* Meta Info */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          {task.due_date && (
            <div className={`flex items-center gap-1 text-xs px-2 py-1 rounded-lg ${isOverdue ? 'bg-red-100 text-red-600' : 'bg-gray-100 text-gray-600'}`}>
              <CalendarDays className="w-3 h-3" />
              {new Date(task.due_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
            </div>
          )}
          {task.estimated_hours && (
            <div className="flex items-center gap-1 text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-lg">
              <Timer className="w-3 h-3" />
              {task.estimated_hours}h
            </div>
          )}
        </div>
        
        <div className="flex items-center gap-1">
          {task.checklist?.length > 0 && (
            <div className="flex items-center gap-1 text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-lg">
              <CheckSquare className="w-3 h-3" />
              {task.checklist.filter(c => c.completed).length}/{task.checklist.length}
            </div>
          )}
          {task.comments?.length > 0 && (
            <div className="flex items-center gap-1 text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-lg">
              <MessageSquare className="w-3 h-3" />
              {task.comments.length}
            </div>
          )}
        </div>
      </div>
      
      {/* Assignees */}
      {task.assignees?.length > 0 && (
        <div className="flex items-center gap-2 mt-3 pt-3 border-t border-gray-100">
          <div className="flex -space-x-2">
            {task.assignees.slice(0, 4).map((assignee, idx) => (
              <div 
                key={assignee.id}
                className="w-7 h-7 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 text-white text-xs flex items-center justify-center ring-2 ring-white"
                title={assignee.username}
              >
                {assignee.username?.charAt(0).toUpperCase()}
              </div>
            ))}
          </div>
          {task.assignees.length > 4 && (
            <span className="text-xs text-gray-500">+{task.assignees.length - 4}</span>
          )}
        </div>
      )}
    </div>
  );
};

// Enhanced Kanban Column
const KanbanColumn = ({ status, tasks, onTaskClick, onDrop }) => {
  const config = STATUS_CONFIG[status];
  const StatusIcon = config.icon;
  const [isDragOver, setIsDragOver] = useState(false);
  
  return (
    <div 
      className={`flex-1 min-w-[320px] max-w-[380px] rounded-xl p-4 transition-all ${isDragOver ? 'ring-2 ring-blue-400 bg-blue-50' : 'bg-gray-50'}`}
      onDragOver={(e) => { e.preventDefault(); setIsDragOver(true); }}
      onDragLeave={() => setIsDragOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setIsDragOver(false);
        const taskId = e.dataTransfer.getData('taskId');
        onDrop(taskId, status);
      }}
    >
      {/* Column Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className={`w-8 h-8 rounded-lg ${config.color} flex items-center justify-center`}>
            <StatusIcon className="w-4 h-4 text-white" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">{config.label}</h3>
            <span className="text-xs text-gray-500">{tasks.length} tasks</span>
          </div>
        </div>
        <Badge variant="secondary" className={`${config.bgLight} ${config.textColor}`}>
          {tasks.length}
        </Badge>
      </div>
      
      {/* Tasks */}
      <div className="space-y-3 min-h-[300px]">
        {tasks.map(task => (
          <TaskCard 
            key={task.id} 
            task={task} 
            onClick={onTaskClick}
          />
        ))}
        {tasks.length === 0 && (
          <div className="flex flex-col items-center justify-center py-8 text-gray-400">
            <Square className="w-8 h-8 mb-2 opacity-50" />
            <span className="text-sm">No tasks</span>
          </div>
        )}
      </div>
    </div>
  );
};

// Gantt Chart View Component
const GanttChartView = ({ tasks, startDate, endDate }) => {
  const sortedTasks = useMemo(() => {
    return [...tasks]
      .filter(t => t.due_date || t.start_date)
      .sort((a, b) => new Date(a.start_date || a.due_date) - new Date(b.start_date || b.due_date));
  }, [tasks]);
  
  const days = useMemo(() => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const daysList = [];
    let current = new Date(start);
    while (current <= end) {
      daysList.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }
    return daysList;
  }, [startDate, endDate]);
  
  const getTaskPosition = (task) => {
    const start = new Date(startDate);
    const taskStart = new Date(task.start_date || task.due_date);
    const taskEnd = task.due_date ? new Date(task.due_date) : new Date(taskStart);
    
    const totalDays = days.length;
    const startOffset = Math.max(0, Math.floor((taskStart - start) / (1000 * 60 * 60 * 24)));
    const duration = Math.max(1, Math.ceil((taskEnd - taskStart) / (1000 * 60 * 60 * 24)) + 1);
    
    return {
      left: `${(startOffset / totalDays) * 100}%`,
      width: `${Math.min((duration / totalDays) * 100, 100 - (startOffset / totalDays) * 100)}%`
    };
  };
  
  const category = (task) => CATEGORY_CONFIG[task.category] || CATEGORY_CONFIG.compliance_task;
  
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center">
            <BarChart3 className="w-5 h-5 text-white" />
          </div>
          <div>
            <span>Gantt Chart</span>
            <p className="text-sm font-normal text-gray-500">Timeline view of safety tasks</p>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {sortedTasks.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <BarChart3 className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>No tasks with dates to display</p>
            <p className="text-sm">Add due dates to tasks to see them on the timeline</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            {/* Timeline Header */}
            <div className="flex border-b border-gray-200 mb-4 pb-2 min-w-[800px]">
              <div className="w-48 flex-shrink-0 font-medium text-gray-700 px-2">Task</div>
              <div className="flex-1 flex">
                {days.slice(0, 14).map((day, idx) => (
                  <div key={idx} className="flex-1 text-center text-xs text-gray-500">
                    <div className="font-medium">{day.toLocaleDateString('en-US', { weekday: 'short' })}</div>
                    <div>{day.getDate()}</div>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Tasks */}
            <div className="space-y-3 min-w-[800px]">
              {sortedTasks.slice(0, 10).map((task) => {
                const cat = category(task);
                const pos = getTaskPosition(task);
                
                return (
                  <div key={task.id} className="flex items-center">
                    <div className="w-48 flex-shrink-0 px-2">
                      <div className="flex items-center gap-2">
                        <span>{cat.emoji}</span>
                        <span className="text-sm font-medium text-gray-900 truncate">{task.title}</span>
                      </div>
                    </div>
                    <div className="flex-1 relative h-8">
                      <div className="absolute inset-0 flex">
                        {days.slice(0, 14).map((_, idx) => (
                          <div key={idx} className="flex-1 border-l border-gray-100" />
                        ))}
                      </div>
                      <div 
                        className={`absolute top-1 h-6 rounded-lg ${cat.color} shadow-sm flex items-center px-2`}
                        style={pos}
                      >
                        <span className="text-xs text-white font-medium truncate">{task.title}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Stats Dashboard Cards
const StatsDashboard = ({ dashboard }) => {
  if (!dashboard?.stats) return null;
  
  const stats = dashboard.stats;
  
  const statCards = [
    { label: 'Total Tasks', value: stats.total_tasks || 0, icon: ClipboardList, color: 'from-blue-500 to-indigo-600', bgLight: 'bg-blue-50' },
    { label: 'In Progress', value: stats.by_status?.in_progress || 0, icon: Play, color: 'from-amber-500 to-orange-500', bgLight: 'bg-amber-50' },
    { label: 'Completed', value: stats.by_status?.completed || 0, icon: CheckCircle2, color: 'from-green-500 to-emerald-500', bgLight: 'bg-green-50' },
    { label: 'Overdue', value: stats.overdue || 0, icon: AlertTriangle, color: 'from-red-500 to-rose-500', bgLight: 'bg-red-50' },
    { label: 'Due This Week', value: stats.due_this_week || 0, icon: CalendarDays, color: 'from-purple-500 to-pink-500', bgLight: 'bg-purple-50' },
    { label: 'My Tasks', value: stats.my_tasks || 0, icon: UserCheck, color: 'from-cyan-500 to-blue-500', bgLight: 'bg-cyan-50' }
  ];
  
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
      {statCards.map((stat, idx) => {
        const Icon = stat.icon;
        return (
          <Card key={idx} className="overflow-hidden">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500 mb-1">{stat.label}</p>
                  <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
                </div>
                <div className={`w-10 h-10 rounded-xl bg-gradient-to-r ${stat.color} flex items-center justify-center shadow-lg`}>
                  <Icon className="w-5 h-5 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};

// Quick Actions Bar with functional links
const QuickActionsBar = ({ onNewTask, onNewProject, onShowTutorial, onViewChange, currentView }) => (
  <div className="flex flex-wrap gap-3 mb-6">
    {FEATURE_HIGHLIGHTS.map((feature, idx) => {
      const Icon = feature.icon;
      const isActive = currentView === feature.action;
      return (
        <div 
          key={idx}
          onClick={() => onViewChange(feature.action)}
          className={`flex items-center gap-2 px-4 py-2 rounded-xl border cursor-pointer hover:shadow-lg transition-all transform hover:-translate-y-0.5 ${
            isActive 
              ? `bg-gradient-to-r ${feature.gradient} text-white border-transparent shadow-lg` 
              : `${feature.bg} border-gray-100 hover:border-gray-200`
          }`}
        >
          <Icon className={`w-5 h-5 ${isActive ? 'text-white' : feature.color}`} />
          <div>
            <p className={`text-sm font-medium ${isActive ? 'text-white' : 'text-gray-900'}`}>{feature.label}</p>
            <p className={`text-xs ${isActive ? 'text-white/80' : 'text-gray-500'}`}>{feature.description}</p>
          </div>
        </div>
      );
    })}
  </div>
);

// Task Detail Modal (Enhanced)
const TaskDetailModal = ({ task, isOpen, onClose, onUpdate, onDelete, users, teams }) => {
  const [editedTask, setEditedTask] = useState(() => task || {});
  const [newComment, setNewComment] = useState('');
  const [newChecklistItem, setNewChecklistItem] = useState('');
  const [activeTab, setActiveTab] = useState('details');
  
  // Update editedTask when task prop changes (using ref to avoid infinite loops)
  const taskRef = React.useRef(task);
  if (task && task !== taskRef.current) {
    taskRef.current = task;
    setEditedTask(task);
  }
  
  if (!task) return null;
  
  const handleSave = () => {
    onUpdate(editedTask);
  };
  
  const handleAddChecklistItem = () => {
    if (!newChecklistItem.trim()) return;
    const newChecklist = [...(editedTask.checklist || []), { text: newChecklistItem, completed: false }];
    setEditedTask({ ...editedTask, checklist: newChecklist });
    setNewChecklistItem('');
  };
  
  const toggleChecklistItem = (index) => {
    const newChecklist = [...editedTask.checklist];
    newChecklist[index].completed = !newChecklist[index].completed;
    setEditedTask({ ...editedTask, checklist: newChecklist });
  };
  
  const category = CATEGORY_CONFIG[editedTask.category] || CATEGORY_CONFIG.compliance_task;
  const checklistProgress = editedTask.checklist?.length > 0 
    ? Math.round((editedTask.checklist.filter(c => c.completed).length / editedTask.checklist.length) * 100)
    : 0;
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className={`-mx-6 -mt-6 px-6 py-4 mb-4 ${category.bgLight} border-b`}>
          <div className="flex items-center gap-3">
            <div className={`w-12 h-12 rounded-xl ${category.color} flex items-center justify-center shadow-lg`}>
              <span className="text-2xl">{category.emoji}</span>
            </div>
            <div className="flex-1">
              <Input 
                value={editedTask.title || ''} 
                onChange={(e) => setEditedTask({ ...editedTask, title: e.target.value })}
                className="text-xl font-bold border-none bg-transparent p-0 h-auto focus-visible:ring-0"
                placeholder="Task title"
              />
              <p className={`text-sm ${category.textColor}`}>{category.label}</p>
            </div>
          </div>
        </div>
        
        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-4">
            <TabsTrigger value="details">Details</TabsTrigger>
            <TabsTrigger value="checklist">Checklist ({editedTask.checklist?.length || 0})</TabsTrigger>
            <TabsTrigger value="comments">Comments ({editedTask.comments?.length || 0})</TabsTrigger>
            <TabsTrigger value="activity">Activity</TabsTrigger>
          </TabsList>
          
          <TabsContent value="details">
            <div className="grid grid-cols-3 gap-6">
              {/* Main Content */}
              <div className="col-span-2 space-y-4">
                <div>
                  <label className="text-sm font-medium text-gray-700">Description</label>
                  <Textarea 
                    value={editedTask.description || ''} 
                    onChange={(e) => setEditedTask({ ...editedTask, description: e.target.value })}
                    className="mt-1"
                    rows={4}
                    placeholder="Add a description..."
                  />
                </div>
                
                {/* Progress */}
                {editedTask.checklist?.length > 0 && (
                  <div className="p-4 bg-gray-50 rounded-xl">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-700">Progress</span>
                      <span className="text-sm font-bold text-gray-900">{checklistProgress}%</span>
                    </div>
                    <Progress value={checklistProgress} className="h-3" />
                  </div>
                )}
              </div>
              
              {/* Sidebar */}
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">Status</label>
                  <Select 
                    value={editedTask.status} 
                    onValueChange={(v) => setEditedTask({ ...editedTask, status: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
                        <SelectItem key={key} value={key}>
                          <span className="flex items-center gap-2">
                            <span>{cfg.emoji}</span>
                            {cfg.label}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">Priority</label>
                  <Select 
                    value={editedTask.priority} 
                    onValueChange={(v) => setEditedTask({ ...editedTask, priority: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(PRIORITY_CONFIG).map(([key, cfg]) => (
                        <SelectItem key={key} value={key}>
                          <span className="flex items-center gap-2">
                            <span>{cfg.emoji}</span>
                            {cfg.label}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">Category</label>
                  <Select 
                    value={editedTask.category} 
                    onValueChange={(v) => setEditedTask({ ...editedTask, category: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(CATEGORY_CONFIG).map(([key, cfg]) => (
                        <SelectItem key={key} value={key}>
                          <span className="flex items-center gap-2">
                            <span>{cfg.emoji}</span>
                            {cfg.label}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">Due Date</label>
                  <Input 
                    type="date"
                    value={editedTask.due_date?.split('T')[0] || ''}
                    onChange={(e) => setEditedTask({ ...editedTask, due_date: e.target.value })}
                  />
                </div>
                
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">Estimated Hours</label>
                  <Input 
                    type="number"
                    min="0"
                    step="0.5"
                    value={editedTask.estimated_hours || ''}
                    onChange={(e) => setEditedTask({ ...editedTask, estimated_hours: parseFloat(e.target.value) || 0 })}
                    placeholder="0"
                  />
                </div>
              </div>
            </div>
          </TabsContent>
          
          <TabsContent value="checklist">
            <div className="space-y-3">
              {editedTask.checklist?.map((item, idx) => (
                <div key={idx} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg group">
                  <button onClick={() => toggleChecklistItem(idx)}>
                    {item.completed ? (
                      <CheckCircle2 className="w-6 h-6 text-green-500" />
                    ) : (
                      <Square className="w-6 h-6 text-gray-400 group-hover:text-blue-500" />
                    )}
                  </button>
                  <span className={`flex-1 ${item.completed ? 'line-through text-gray-400' : 'text-gray-700'}`}>
                    {item.text}
                  </span>
                </div>
              ))}
              <div className="flex items-center gap-2 mt-4">
                <Input 
                  placeholder="Add checklist item..."
                  value={newChecklistItem}
                  onChange={(e) => setNewChecklistItem(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleAddChecklistItem()}
                  className="flex-1"
                />
                <Button onClick={handleAddChecklistItem}>
                  <Plus className="w-4 h-4 mr-1" />
                  Add
                </Button>
              </div>
            </div>
          </TabsContent>
          
          <TabsContent value="comments">
            <div className="space-y-4">
              {editedTask.comments?.map((comment, idx) => (
                <div key={idx} className="p-4 bg-gray-50 rounded-xl">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-8 h-8 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 flex items-center justify-center text-white text-sm font-medium">
                      {comment.user_name?.charAt(0) || 'U'}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">{comment.user_name || 'User'}</p>
                      <p className="text-xs text-gray-500">{new Date(comment.created_at).toLocaleString()}</p>
                    </div>
                  </div>
                  <p className="text-gray-700">{comment.content}</p>
                </div>
              ))}
              <Textarea 
                placeholder="Add a comment..."
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                rows={3}
              />
              <Button className="w-full">
                <MessageSquare className="w-4 h-4 mr-2" />
                Post Comment
              </Button>
            </div>
          </TabsContent>
          
          <TabsContent value="activity">
            <div className="space-y-3">
              {editedTask.activity_log?.map((activity, idx) => (
                <div key={idx} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                  <Activity className="w-5 h-5 text-blue-500 mt-0.5" />
                  <div>
                    <p className="text-sm text-gray-700">
                      <span className="font-medium">{activity.user_name || 'User'}</span>
                      {' '}{activity.action}
                    </p>
                    <p className="text-xs text-gray-500">{new Date(activity.timestamp).toLocaleString()}</p>
                  </div>
                </div>
              ))}
              {(!editedTask.activity_log || editedTask.activity_log.length === 0) && (
                <div className="text-center py-8 text-gray-500">
                  <Activity className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p>No activity yet</p>
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
        
        <DialogFooter className="mt-6">
          <Button variant="destructive" onClick={() => onDelete(task.id)}>
            <Trash2 className="w-4 h-4 mr-2" />
            Delete
          </Button>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave} className="bg-gradient-to-r from-blue-500 to-indigo-500">
            <CheckCircle2 className="w-4 h-4 mr-2" />
            Save Changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Create Task Modal (Enhanced)
const CreateTaskModal = ({ isOpen, onClose, onCreate, projects, teams, templates }) => {
  const [taskData, setTaskData] = useState({
    title: '',
    description: '',
    category: 'compliance_task',
    priority: 'medium',
    status: 'todo',
    project_id: '',
    team_id: '',
    due_date: '',
    start_date: '',
    estimated_hours: '',
    location: '',
    checklist: []
  });
  
  const handleCreate = () => {
    onCreate(taskData);
    setTaskData({
      title: '',
      description: '',
      category: 'compliance_task',
      priority: 'medium',
      status: 'todo',
      project_id: '',
      team_id: '',
      due_date: '',
      start_date: '',
      estimated_hours: '',
      location: '',
      checklist: []
    });
  };
  
  const selectedCategory = CATEGORY_CONFIG[taskData.category];
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-xl ${selectedCategory?.color || 'bg-blue-500'} flex items-center justify-center`}>
              <Plus className="w-5 h-5 text-white" />
            </div>
            Create New Safety Task
          </DialogTitle>
          <DialogDescription>
            Add a new task to track safety work
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 mt-4">
          <div>
            <label className="text-sm font-medium">Title *</label>
            <Input 
              value={taskData.title}
              onChange={(e) => setTaskData({ ...taskData, title: e.target.value })}
              placeholder="Enter task title..."
              className="mt-1"
            />
          </div>
          
          <div>
            <label className="text-sm font-medium">Description</label>
            <Textarea 
              value={taskData.description}
              onChange={(e) => setTaskData({ ...taskData, description: e.target.value })}
              placeholder="Describe the task..."
              className="mt-1"
              rows={2}
            />
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium">Category</label>
              <Select 
                value={taskData.category}
                onValueChange={(v) => setTaskData({ ...taskData, category: v })}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(CATEGORY_CONFIG).map(([key, cfg]) => (
                    <SelectItem key={key} value={key}>
                      <span className="flex items-center gap-2">
                        <span>{cfg.emoji}</span>
                        {cfg.label}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <label className="text-sm font-medium">Priority</label>
              <Select 
                value={taskData.priority}
                onValueChange={(v) => setTaskData({ ...taskData, priority: v })}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(PRIORITY_CONFIG).map(([key, cfg]) => (
                    <SelectItem key={key} value={key}>
                      <span className="flex items-center gap-2">
                        <span>{cfg.emoji}</span>
                        {cfg.label}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium">Project</label>
              <Select 
                value={taskData.project_id || "none"}
                onValueChange={(v) => setTaskData({ ...taskData, project_id: v === "none" ? "" : v })}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select project" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No Project</SelectItem>
                  {projects.map(p => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <label className="text-sm font-medium">Team</label>
              <Select 
                value={taskData.team_id || "none"}
                onValueChange={(v) => setTaskData({ ...taskData, team_id: v === "none" ? "" : v })}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select team" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No Team</SelectItem>
                  {teams.map(t => (
                    <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="text-sm font-medium">Start Date</label>
              <Input 
                type="date"
                value={taskData.start_date}
                onChange={(e) => setTaskData({ ...taskData, start_date: e.target.value })}
                className="mt-1"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Due Date</label>
              <Input 
                type="date"
                value={taskData.due_date}
                onChange={(e) => setTaskData({ ...taskData, due_date: e.target.value })}
                className="mt-1"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Est. Hours</label>
              <Input 
                type="number"
                min="0"
                step="0.5"
                value={taskData.estimated_hours}
                onChange={(e) => setTaskData({ ...taskData, estimated_hours: e.target.value })}
                placeholder="0"
                className="mt-1"
              />
            </div>
          </div>
        </div>
        
        <DialogFooter className="mt-6">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleCreate} disabled={!taskData.title} className="bg-gradient-to-r from-blue-500 to-indigo-500">
            <Plus className="w-4 h-4 mr-2" />
            Create Task
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Create Project Modal (Enhanced)
const CreateProjectModal = ({ isOpen, onClose, onCreate, teams, templates }) => {
  const [projectData, setProjectData] = useState({
    name: '',
    description: '',
    team_id: '',
    color: '#3B82F6',
    template_type: ''
  });
  
  const handleCreate = () => {
    onCreate(projectData);
    setProjectData({ name: '', description: '', team_id: '', color: '#3B82F6', template_type: '' });
  };
  
  const colors = [
    { value: '#3B82F6', label: 'Blue' },
    { value: '#10B981', label: 'Green' },
    { value: '#F59E0B', label: 'Amber' },
    { value: '#EF4444', label: 'Red' },
    { value: '#8B5CF6', label: 'Purple' },
    { value: '#EC4899', label: 'Pink' },
    { value: '#06B6D4', label: 'Cyan' },
    { value: '#84CC16', label: 'Lime' }
  ];
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center">
              <Folder className="w-5 h-5 text-white" />
            </div>
            Create Safety Project
          </DialogTitle>
          <DialogDescription>
            Start a new safety initiative or use a template
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 mt-4">
          <div>
            <label className="text-sm font-medium">Project Name *</label>
            <Input 
              value={projectData.name}
              onChange={(e) => setProjectData({ ...projectData, name: e.target.value })}
              placeholder="e.g., Q1 Safety Inspection Program"
              className="mt-1"
            />
          </div>
          
          <div>
            <label className="text-sm font-medium">Description</label>
            <Textarea 
              value={projectData.description}
              onChange={(e) => setProjectData({ ...projectData, description: e.target.value })}
              placeholder="Describe the project goals..."
              className="mt-1"
              rows={2}
            />
          </div>
          
          <div>
            <label className="text-sm font-medium">Start from Template</label>
            <Select 
              value={projectData.template_type || "none"}
              onValueChange={(v) => setProjectData({ ...projectData, template_type: v === "none" ? "" : v })}
            >
              <SelectTrigger className="mt-1">
                <SelectValue placeholder="No template (blank project)" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Blank Project</SelectItem>
                {templates.map(t => (
                  <SelectItem key={t.id} value={t.id}>
                    <span className="flex items-center gap-2">
                      <Sparkles className="w-4 h-4 text-amber-500" />
                      {t.name} ({t.task_count} tasks)
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div>
            <label className="text-sm font-medium">Color</label>
            <div className="flex gap-2 mt-2">
              {colors.map(c => (
                <button
                  key={c.value}
                  className={`w-8 h-8 rounded-lg transition-all ${projectData.color === c.value ? 'ring-2 ring-offset-2 ring-blue-500 scale-110' : 'hover:scale-105'}`}
                  style={{ backgroundColor: c.value }}
                  onClick={() => setProjectData({ ...projectData, color: c.value })}
                  title={c.label}
                />
              ))}
            </div>
          </div>
        </div>
        
        <DialogFooter className="mt-6">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleCreate} disabled={!projectData.name} className="bg-gradient-to-r from-purple-500 to-pink-500">
            <Sparkles className="w-4 h-4 mr-2" />
            Create Project
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Main Component
const SafetyWorkManagement = () => {
  const [view, setView] = useState('board');
  const [tasks, setTasks] = useState([]);
  const [projects, setProjects] = useState([]);
  const [teams, setTeams] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [users, setUsers] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  
  const [selectedProject, setSelectedProject] = useState('all');
  const [selectedTeam, setSelectedTeam] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [showCreateTaskModal, setShowCreateTaskModal] = useState(false);
  const [showCreateProjectModal, setShowCreateProjectModal] = useState(false);
  const [showTutorial, setShowTutorial] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  
  const [calendarDate, setCalendarDate] = useState(new Date());
  
  // Check if first visit
  useEffect(() => {
    const hasSeenTutorial = localStorage.getItem('safety_work_tutorial_seen');
    if (!hasSeenTutorial) {
      setShowTutorial(true);
    }
  }, []);
  
  const handleTutorialComplete = () => {
    localStorage.setItem('safety_work_tutorial_seen', 'true');
  };
  
  const token = localStorage.getItem('auth_token');
  const headers = { Authorization: `Bearer ${token}` };
  
  // Fetch data
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [tasksRes, projectsRes, teamsRes, templatesRes, usersRes, dashboardRes] = await Promise.all([
        axios.get(`${API_URL}/api/safety-tasks`, { headers }),
        axios.get(`${API_URL}/api/safety-projects`, { headers }),
        axios.get(`${API_URL}/api/safety-teams`, { headers }),
        axios.get(`${API_URL}/api/safety-projects/templates`, { headers }),
        axios.get(`${API_URL}/api/company-users`, { headers }),
        axios.get(`${API_URL}/api/safety-work/dashboard`, { headers })
      ]);
      
      setTasks(tasksRes.data.tasks || []);
      setProjects(projectsRes.data.projects || []);
      setTeams(teamsRes.data.teams || []);
      setTemplates(templatesRes.data.templates || []);
      setUsers(usersRes.data.users || []);
      setDashboard(dashboardRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, []);
  
  useEffect(() => {
    fetchData();
  }, [fetchData]);
  
  // Filter tasks
  const filteredTasks = tasks.filter(task => {
    if (selectedProject !== 'all' && task.project_id !== selectedProject) return false;
    if (selectedTeam !== 'all' && task.team_id !== selectedTeam) return false;
    if (searchQuery && !task.title.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    return true;
  });
  
  // Group tasks by status for Kanban
  const tasksByStatus = {
    todo: filteredTasks.filter(t => t.status === 'todo'),
    in_progress: filteredTasks.filter(t => t.status === 'in_progress'),
    review: filteredTasks.filter(t => t.status === 'review'),
    completed: filteredTasks.filter(t => t.status === 'completed'),
    blocked: filteredTasks.filter(t => t.status === 'blocked')
  };
  
  // Handlers
  const handleCreateTask = async (taskData) => {
    try {
      await axios.post(`${API_URL}/api/safety-tasks`, taskData, { headers });
      fetchData();
      setShowCreateTaskModal(false);
    } catch (error) {
      console.error('Error creating task:', error);
    }
  };
  
  const handleUpdateTask = async (taskData) => {
    try {
      const { id, assignees, linked_audit, linked_incident, comments, activity_log, ...updateData } = taskData;
      await axios.put(`${API_URL}/api/safety-tasks/${id}`, updateData, { headers });
      fetchData();
      setShowTaskModal(false);
    } catch (error) {
      console.error('Error updating task:', error);
    }
  };
  
  const handleDeleteTask = async (taskId) => {
    if (!window.confirm('Are you sure you want to delete this task?')) return;
    try {
      await axios.delete(`${API_URL}/api/safety-tasks/${taskId}`, { headers });
      fetchData();
      setShowTaskModal(false);
    } catch (error) {
      console.error('Error deleting task:', error);
    }
  };
  
  const handleDropTask = async (taskId, newStatus) => {
    try {
      await axios.put(`${API_URL}/api/safety-tasks/${taskId}`, { status: newStatus }, { headers });
      setTasks(prev => prev.map(t => t.id === taskId ? { ...t, status: newStatus } : t));
    } catch (error) {
      console.error('Error updating task status:', error);
    }
  };
  
  const handleCreateProject = async (projectData) => {
    try {
      await axios.post(`${API_URL}/api/safety-projects`, projectData, { headers });
      fetchData();
      setShowCreateProjectModal(false);
    } catch (error) {
      console.error('Error creating project:', error);
    }
  };
  
  // Calendar helpers
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const days = new Date(year, month + 1, 0).getDate();
    const firstDay = new Date(year, month, 1).getDay();
    return { days, firstDay };
  };
  
  const { days, firstDay } = getDaysInMonth(calendarDate);
  
  const getTasksForDate = (day) => {
    const dateStr = `${calendarDate.getFullYear()}-${String(calendarDate.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return filteredTasks.filter(t => t.due_date?.startsWith(dateStr));
  };
  
  // Gantt chart date range
  const ganttStartDate = new Date();
  const ganttEndDate = new Date();
  ganttEndDate.setDate(ganttEndDate.getDate() + 14);
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Tutorial Modal */}
      <TutorialModal 
        isOpen={showTutorial}
        onClose={() => setShowTutorial(false)}
        onComplete={handleTutorialComplete}
      />
      
      {/* Header */}
      <div className="relative rounded-b-3xl overflow-hidden bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 text-white shadow-2xl">
        <div className="absolute inset-0 opacity-20">
          <img 
            src={HEADER_IMAGE_URL}
            alt="Team collaborating on safety work"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-r from-indigo-600/80 via-purple-600/80 to-pink-600/80" />
        
        <div className="relative px-4 md:px-8 py-6 md:py-10">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div className="flex items-center gap-3 md:gap-4">
                <div className="w-12 h-12 md:w-16 md:h-16 bg-white/20 backdrop-blur rounded-xl md:rounded-2xl flex items-center justify-center shadow-lg">
                  <Layers className="w-6 h-6 md:w-8 md:h-8 text-white" />
                </div>
                <div>
                  <h1 className="text-2xl md:text-4xl font-bold mb-0.5 md:mb-1">Safety Work Management</h1>
                  <p className="text-white/80 text-sm md:text-lg">Collaborate on safety tasks, procedures, and workflows</p>
                </div>
              </div>
              {/* Mobile action buttons */}
              <div className="flex md:hidden items-center gap-2">
                <Button 
                  size="sm"
                  onClick={() => setShowCreateTaskModal(true)}
                  className="bg-white text-purple-600 hover:bg-white/90 flex-1"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Task
                </Button>
                <Button 
                  size="sm"
                  onClick={() => setShowCreateProjectModal(true)}
                  className="bg-white/20 backdrop-blur hover:bg-white/30 text-white border border-white/30 flex-1"
                >
                  <Folder className="w-4 h-4 mr-1" />
                  Project
                </Button>
              </div>
              {/* Desktop action buttons */}
              <div className="hidden md:flex items-center gap-3">
                <Link to="/meetings?category=safety_meeting">
                  <Button 
                    className="bg-green-500 hover:bg-green-600 text-white"
                  >
                    <Video className="w-4 h-4 mr-2" />
                    Schedule Meeting
                  </Button>
                </Link>
                <Button 
                  variant="ghost"
                  onClick={() => setShowTutorial(true)}
                  className="text-white hover:bg-white/20"
                >
                  <HelpCircle className="w-4 h-4 mr-2" />
                  Tutorial
                </Button>
                <Button 
                  onClick={() => setShowCreateProjectModal(true)}
                  className="bg-white text-purple-600 hover:bg-white/90"
                >
                  <Folder className="w-4 h-4 mr-2" />
                  New Project
                </Button>
                <Button 
                  onClick={() => setShowCreateTaskModal(true)}
                  className="bg-white/20 backdrop-blur hover:bg-white/30 text-white border border-white/30"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  New Task
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-4 md:py-6">
        {/* Stats Dashboard */}
        <StatsDashboard dashboard={dashboard} />
        
        {/* Quick Actions - Hidden on mobile, shown on larger screens */}
        <div className="hidden lg:block">
          <QuickActionsBar 
            onNewTask={() => setShowCreateTaskModal(true)}
            onNewProject={() => setShowCreateProjectModal(true)}
            onShowTutorial={() => setShowTutorial(true)}
            onViewChange={(newView) => setView(newView)}
            currentView={view}
          />
        </div>
        
        {/* Toolbar */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 md:gap-4 mb-4 md:mb-6 bg-white rounded-xl md:rounded-2xl p-3 md:p-4 shadow-sm">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            {/* View Toggle - Scrollable on mobile */}
            <div className="flex bg-gray-100 rounded-xl p-1 overflow-x-auto">
              {[
                { id: 'board', icon: LayoutGrid, label: 'Board' },
                { id: 'list', icon: List, label: 'List' },
                { id: 'gantt', icon: BarChart3, label: 'Gantt' },
                { id: 'calendar', icon: Calendar, label: 'Calendar' },
                { id: 'teams', icon: Users, label: 'Teams' },
                { id: 'analytics', icon: PieChart, label: 'Analytics' },
                { id: 'templates', icon: Sparkles, label: 'Templates' }
              ].map(v => (
                <button
                  key={v.id}
                  onClick={() => setView(v.id)}
                  className={`px-2 md:px-3 py-2 rounded-lg flex items-center gap-1.5 text-xs md:text-sm font-medium transition-all whitespace-nowrap ${
                    view === v.id 
                      ? 'bg-white text-purple-600 shadow-sm' 
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <v.icon className="w-4 h-4" />
                  <span className="hidden sm:inline">{v.label}</span>
                </button>
              ))}
            </div>
            
            {/* Search */}
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <Input 
                placeholder="Search tasks..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 w-full sm:w-48 md:w-64 rounded-xl"
              />
            </div>
          </div>
          
          <div className="flex items-center gap-2 md:gap-3">
            {/* Filters */}
            <Select value={selectedProject} onValueChange={setSelectedProject}>
              <SelectTrigger className="w-full sm:w-32 md:w-40 rounded-xl text-sm">
                <Folder className="w-4 h-4 mr-1 md:mr-2 text-gray-400 flex-shrink-0" />
                <SelectValue placeholder="Projects" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Projects</SelectItem>
                {projects.map(p => (
                  <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select value={selectedTeam} onValueChange={setSelectedTeam}>
              <SelectTrigger className="w-full sm:w-32 md:w-40 rounded-xl text-sm">
                <Users className="w-4 h-4 mr-1 md:mr-2 text-gray-400 flex-shrink-0" />
                <SelectValue placeholder="Teams" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Teams</SelectItem>
                {teams.map(t => (
                  <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        
        {/* Main Content */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
          </div>
        ) : (
          <>
            {/* Kanban Board View */}
            {view === 'board' && (
              <div className="flex gap-3 md:gap-4 overflow-x-auto pb-4 -mx-4 px-4 md:mx-0 md:px-0">
                {['todo', 'in_progress', 'review', 'completed', 'blocked'].map(status => (
                  <KanbanColumn
                    key={status}
                    status={status}
                    tasks={tasksByStatus[status]}
                    onTaskClick={(task) => { setSelectedTask(task); setShowTaskModal(true); }}
                    onDrop={handleDropTask}
                  />
                ))}
              </div>
            )}
            
            {/* List View */}
            {view === 'list' && (
              <Card className="rounded-xl md:rounded-2xl shadow-sm overflow-hidden">
                <CardContent className="p-0 overflow-x-auto">
                  <table className="w-full min-w-[800px]">
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        <th className="text-left px-4 md:px-6 py-3 md:py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Task</th>
                        <th className="text-left px-4 md:px-6 py-3 md:py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Category</th>
                        <th className="text-left px-4 md:px-6 py-3 md:py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                        <th className="text-left px-4 md:px-6 py-3 md:py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Priority</th>
                        <th className="text-left px-4 md:px-6 py-3 md:py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Due Date</th>
                        <th className="text-left px-4 md:px-6 py-3 md:py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Progress</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-100">
                      {filteredTasks.map(task => {
                        const category = CATEGORY_CONFIG[task.category] || CATEGORY_CONFIG.compliance_task;
                        const status = STATUS_CONFIG[task.status];
                        const priority = PRIORITY_CONFIG[task.priority];
                        const progress = task.checklist?.length > 0 
                          ? Math.round((task.checklist.filter(c => c.completed).length / task.checklist.length) * 100)
                          : 0;
                        
                        return (
                          <tr 
                            key={task.id} 
                            className="hover:bg-gray-50 cursor-pointer transition-colors"
                            onClick={() => { setSelectedTask(task); setShowTaskModal(true); }}
                          >
                            <td className="px-4 md:px-6 py-3 md:py-4">
                              <div className="flex items-center gap-2 md:gap-3">
                                <span className="text-base md:text-lg">{category.emoji}</span>
                                <span className="font-medium text-gray-900 text-sm md:text-base">{task.title}</span>
                              </div>
                            </td>
                            <td className="px-4 md:px-6 py-3 md:py-4">
                              <span className={`text-xs md:text-sm px-2 md:px-3 py-1 rounded-lg ${category.bgLight} ${category.textColor}`}>
                                {category.label}
                              </span>
                            </td>
                            <td className="px-4 md:px-6 py-3 md:py-4">
                              <span className={`text-xs md:text-sm px-2 md:px-3 py-1 rounded-lg ${status.bgLight} ${status.textColor}`}>
                                {status.emoji} {status.label}
                              </span>
                            </td>
                            <td className="px-4 md:px-6 py-3 md:py-4">
                              <span className={`text-xs md:text-sm px-2 md:px-3 py-1 rounded-lg ${priority.bgLight} ${priority.textColor}`}>
                                {priority.emoji} {priority.label}
                              </span>
                            </td>
                            <td className="px-4 md:px-6 py-3 md:py-4 text-xs md:text-sm text-gray-600">
                              {task.due_date ? new Date(task.due_date).toLocaleDateString() : '-'}
                            </td>
                            <td className="px-4 md:px-6 py-3 md:py-4">
                              <div className="w-20 md:w-24">
                                <Progress value={progress} className="h-2" />
                                <span className="text-xs text-gray-500">{progress}%</span>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  {filteredTasks.length === 0 && (
                    <div className="text-center py-12 text-gray-500">
                      <ClipboardList className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p className="font-medium">No tasks found</p>
                      <p className="text-sm">Create your first task to get started!</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
            
            {/* Gantt Chart View */}
            {view === 'gantt' && (
              <GanttChartView 
                tasks={filteredTasks}
                startDate={ganttStartDate}
                endDate={ganttEndDate}
              />
            )}
            
            {/* Calendar View */}
            {view === 'calendar' && (
              <Card className="rounded-2xl shadow-sm">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center gap-2">
                      <Calendar className="w-5 h-5 text-purple-500" />
                      {calendarDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                    </CardTitle>
                    <div className="flex items-center gap-2">
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => setCalendarDate(new Date(calendarDate.setMonth(calendarDate.getMonth() - 1)))}
                        className="rounded-lg"
                      >
                        <ChevronLeft className="w-4 h-4" />
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => setCalendarDate(new Date())}
                        className="rounded-lg"
                      >
                        Today
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => setCalendarDate(new Date(calendarDate.setMonth(calendarDate.getMonth() + 1)))}
                        className="rounded-lg"
                      >
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-7 gap-px bg-gray-200 rounded-xl overflow-hidden">
                    {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                      <div key={day} className="bg-gray-50 p-3 text-center text-sm font-semibold text-gray-600">
                        {day}
                      </div>
                    ))}
                    {Array.from({ length: firstDay }).map((_, i) => (
                      <div key={`empty-${i}`} className="bg-white p-2 min-h-[100px]"></div>
                    ))}
                    {Array.from({ length: days }).map((_, i) => {
                      const day = i + 1;
                      const dayTasks = getTasksForDate(day);
                      const isToday = new Date().toDateString() === new Date(calendarDate.getFullYear(), calendarDate.getMonth(), day).toDateString();
                      
                      return (
                        <div 
                          key={day} 
                          className={`bg-white p-2 min-h-[100px] ${isToday ? 'ring-2 ring-purple-500 ring-inset' : ''}`}
                        >
                          <div className={`text-sm font-medium mb-1 ${isToday ? 'w-7 h-7 rounded-full bg-purple-500 text-white flex items-center justify-center' : 'text-gray-700'}`}>
                            {day}
                          </div>
                          <div className="space-y-1">
                            {dayTasks.slice(0, 3).map(task => {
                              const category = CATEGORY_CONFIG[task.category];
                              return (
                                <div 
                                  key={task.id}
                                  className={`text-xs p-1.5 rounded-lg truncate cursor-pointer hover:opacity-80 text-white ${category?.color || 'bg-gray-500'}`}
                                  onClick={() => { setSelectedTask(task); setShowTaskModal(true); }}
                                  title={task.title}
                                >
                                  {category?.emoji} {task.title}
                                </div>
                              );
                            })}
                            {dayTasks.length > 3 && (
                              <div className="text-xs text-gray-500 font-medium">+{dayTasks.length - 3} more</div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            )}
            
            {/* Teams Management View */}
            {view === 'teams' && (
              <div className="space-y-6">
                {/* Teams Header */}
                <Card className="rounded-2xl shadow-sm overflow-hidden">
                  <div className="bg-gradient-to-r from-orange-500 to-amber-500 p-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-14 h-14 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
                          <Users className="w-7 h-7 text-white" />
                        </div>
                        <div>
                          <h2 className="text-2xl font-bold text-white">Team Management</h2>
                          <p className="text-orange-100">Manage your safety teams and role assignments</p>
                        </div>
                      </div>
                      <Button className="bg-white text-orange-600 hover:bg-orange-50">
                        <UserPlus className="w-4 h-4 mr-2" />
                        Create Team
                      </Button>
                    </div>
                  </div>
                </Card>

                {/* Teams Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {teams.length > 0 ? teams.map(team => (
                    <Card key={team.id} className="rounded-2xl shadow-sm hover:shadow-lg transition-all cursor-pointer group">
                      <CardContent className="p-6">
                        <div className="flex items-center gap-3 mb-4">
                          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-orange-500 to-amber-500 flex items-center justify-center shadow-lg">
                            <Users className="w-6 h-6 text-white" />
                          </div>
                          <div>
                            <h3 className="font-bold text-gray-900 group-hover:text-orange-600 transition-colors">{team.name}</h3>
                            <p className="text-sm text-gray-500">{team.members?.length || 0} members</p>
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {team.members?.slice(0, 4).map((member, idx) => (
                            <div key={idx} className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-xs font-medium text-gray-600 border-2 border-white shadow-sm">
                              {member.name?.charAt(0) || '?'}
                            </div>
                          ))}
                          {team.members?.length > 4 && (
                            <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center text-xs font-medium text-orange-600 border-2 border-white shadow-sm">
                              +{team.members.length - 4}
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  )) : (
                    <Card className="rounded-2xl shadow-sm col-span-full">
                      <CardContent className="p-12 text-center">
                        <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                        <h3 className="text-xl font-semibold text-gray-900 mb-2">No Teams Yet</h3>
                        <p className="text-gray-500 mb-4">Create your first safety team to start organizing work</p>
                        <Button className="bg-gradient-to-r from-orange-500 to-amber-500">
                          <UserPlus className="w-4 h-4 mr-2" />
                          Create First Team
                        </Button>
                      </CardContent>
                    </Card>
                  )}
                </div>

                {/* Role Assignments Overview */}
                <Card className="rounded-2xl shadow-sm">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <UserCheck className="w-5 h-5 text-orange-500" />
                      Role Assignments
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
                      {Object.entries(ROLE_CONFIG).map(([key, role]) => {
                        const RoleIcon = role.icon;
                        const count = users.filter(u => u.role === key).length;
                        return (
                          <div key={key} className="p-4 rounded-xl bg-gray-50 hover:bg-gray-100 transition-all cursor-pointer text-center">
                            <div className={`w-10 h-10 rounded-lg ${role.color} flex items-center justify-center mx-auto mb-2`}>
                              <RoleIcon className="w-5 h-5 text-white" />
                            </div>
                            <p className="text-2xl font-bold text-gray-900">{count}</p>
                            <p className="text-xs text-gray-500">{role.label}</p>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Analytics View */}
            {view === 'analytics' && (
              <div className="space-y-6">
                {/* Analytics Header */}
                <Card className="rounded-2xl shadow-sm overflow-hidden">
                  <div className="bg-gradient-to-r from-pink-500 to-rose-500 p-6">
                    <div className="flex items-center gap-4">
                      <div className="w-14 h-14 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
                        <PieChart className="w-7 h-7 text-white" />
                      </div>
                      <div>
                        <h2 className="text-2xl font-bold text-white">Safety Analytics Dashboard</h2>
                        <p className="text-pink-100">Track progress and identify trends in your safety program</p>
                      </div>
                    </div>
                  </div>
                </Card>

                {/* Analytics Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <Card className="rounded-2xl shadow-sm">
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between mb-4">
                        <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                          <ClipboardList className="w-6 h-6 text-blue-600" />
                        </div>
                        <Badge className="bg-blue-100 text-blue-700">Total</Badge>
                      </div>
                      <p className="text-3xl font-bold text-gray-900">{tasks.length}</p>
                      <p className="text-sm text-gray-500">Total Tasks</p>
                    </CardContent>
                  </Card>
                  
                  <Card className="rounded-2xl shadow-sm">
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between mb-4">
                        <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center">
                          <CheckCircle2 className="w-6 h-6 text-green-600" />
                        </div>
                        <Badge className="bg-green-100 text-green-700">Done</Badge>
                      </div>
                      <p className="text-3xl font-bold text-gray-900">{tasks.filter(t => t.status === 'completed').length}</p>
                      <p className="text-sm text-gray-500">Completed</p>
                    </CardContent>
                  </Card>
                  
                  <Card className="rounded-2xl shadow-sm">
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between mb-4">
                        <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center">
                          <Clock className="w-6 h-6 text-amber-600" />
                        </div>
                        <Badge className="bg-amber-100 text-amber-700">Active</Badge>
                      </div>
                      <p className="text-3xl font-bold text-gray-900">{tasks.filter(t => t.status === 'in_progress').length}</p>
                      <p className="text-sm text-gray-500">In Progress</p>
                    </CardContent>
                  </Card>
                  
                  <Card className="rounded-2xl shadow-sm">
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between mb-4">
                        <div className="w-12 h-12 rounded-xl bg-red-100 flex items-center justify-center">
                          <AlertTriangle className="w-6 h-6 text-red-600" />
                        </div>
                        <Badge className="bg-red-100 text-red-700">Urgent</Badge>
                      </div>
                      <p className="text-3xl font-bold text-gray-900">{tasks.filter(t => t.priority === 'critical' || t.priority === 'immediate').length}</p>
                      <p className="text-sm text-gray-500">Critical Priority</p>
                    </CardContent>
                  </Card>
                </div>

                {/* Charts Row */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Tasks by Status */}
                  <Card className="rounded-2xl shadow-sm">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Activity className="w-5 h-5 text-pink-500" />
                        Tasks by Status
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        {Object.entries(STATUS_CONFIG).map(([key, config]) => {
                          const count = tasks.filter(t => t.status === key).length;
                          const percentage = tasks.length > 0 ? Math.round((count / tasks.length) * 100) : 0;
                          return (
                            <div key={key} className="flex items-center gap-4">
                              <div className={`w-10 h-10 rounded-lg ${config.color} flex items-center justify-center`}>
                                <span className="text-white">{config.emoji}</span>
                              </div>
                              <div className="flex-1">
                                <div className="flex justify-between mb-1">
                                  <span className="text-sm font-medium text-gray-700">{config.label}</span>
                                  <span className="text-sm font-medium text-gray-900">{count} ({percentage}%)</span>
                                </div>
                                <Progress value={percentage} className="h-2" />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </CardContent>
                  </Card>

                  {/* Tasks by Category */}
                  <Card className="rounded-2xl shadow-sm">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Layers className="w-5 h-5 text-pink-500" />
                        Tasks by Category
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-2 gap-3">
                        {Object.entries(CATEGORY_CONFIG).slice(0, 8).map(([key, config]) => {
                          const count = tasks.filter(t => t.category === key).length;
                          return (
                            <div key={key} className={`p-3 rounded-xl ${config.bgLight} flex items-center gap-3`}>
                              <span className="text-xl">{config.emoji}</span>
                              <div>
                                <p className="text-lg font-bold text-gray-900">{count}</p>
                                <p className="text-xs text-gray-500">{config.label}</p>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Completion Rate */}
                <Card className="rounded-2xl shadow-sm">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <TrendingUp className="w-5 h-5 text-pink-500" />
                      Overall Progress
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center gap-6">
                      <div className="w-32 h-32 relative">
                        <svg className="w-full h-full transform -rotate-90">
                          <circle cx="64" cy="64" r="56" fill="none" stroke="#f3f4f6" strokeWidth="12" />
                          <circle 
                            cx="64" cy="64" r="56" fill="none" 
                            stroke="url(#progressGradient)" 
                            strokeWidth="12" 
                            strokeLinecap="round"
                            strokeDasharray={`${(tasks.filter(t => t.status === 'completed').length / Math.max(tasks.length, 1)) * 352} 352`}
                          />
                          <defs>
                            <linearGradient id="progressGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                              <stop offset="0%" stopColor="#ec4899" />
                              <stop offset="100%" stopColor="#f43f5e" />
                            </linearGradient>
                          </defs>
                        </svg>
                        <div className="absolute inset-0 flex items-center justify-center">
                          <span className="text-2xl font-bold text-gray-900">
                            {tasks.length > 0 ? Math.round((tasks.filter(t => t.status === 'completed').length / tasks.length) * 100) : 0}%
                          </span>
                        </div>
                      </div>
                      <div className="flex-1">
                        <h3 className="text-xl font-bold text-gray-900 mb-2">Completion Rate</h3>
                        <p className="text-gray-500 mb-4">
                          {tasks.filter(t => t.status === 'completed').length} of {tasks.length} tasks completed
                        </p>
                        <div className="flex gap-4">
                          <div className="flex items-center gap-2">
                            <div className="w-3 h-3 rounded-full bg-green-500"></div>
                            <span className="text-sm text-gray-600">Completed</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="w-3 h-3 rounded-full bg-gray-200"></div>
                            <span className="text-sm text-gray-600">Remaining</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Templates View */}
            {view === 'templates' && (
              <div className="space-y-6">
                {/* Templates Header */}
                <Card className="rounded-2xl shadow-sm overflow-hidden">
                  <div className="bg-gradient-to-r from-amber-500 to-yellow-500 p-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-14 h-14 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
                          <Sparkles className="w-7 h-7 text-white" />
                        </div>
                        <div>
                          <h2 className="text-2xl font-bold text-white">Safety Workflow Templates</h2>
                          <p className="text-amber-100">Pre-built workflows designed by safety experts</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </Card>

                {/* Templates Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {templates.map(template => {
                    const categoryKey = template.category || 'compliance_task';
                    const category = CATEGORY_CONFIG[categoryKey] || CATEGORY_CONFIG.compliance_task;
                    return (
                      <Card key={template.id} className="rounded-2xl shadow-sm hover:shadow-xl transition-all cursor-pointer group overflow-hidden">
                        <div className={`h-2 bg-gradient-to-r ${category.color}`}></div>
                        <CardContent className="p-6">
                          <div className="flex items-start gap-4 mb-4">
                            <div className={`w-14 h-14 rounded-2xl ${category.color} flex items-center justify-center shadow-lg flex-shrink-0`}>
                              <span className="text-2xl">{category.emoji}</span>
                            </div>
                            <div>
                              <h3 className="font-bold text-gray-900 group-hover:text-amber-600 transition-colors mb-1">{template.name}</h3>
                              <p className="text-sm text-gray-500 line-clamp-2">{template.description}</p>
                            </div>
                          </div>
                          
                          <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                            <div className="flex items-center gap-2">
                              <ClipboardList className="w-4 h-4 text-gray-400" />
                              <span className="text-sm text-gray-600">{template.task_count} tasks</span>
                            </div>
                            <Button 
                              size="sm" 
                              className="bg-gradient-to-r from-amber-500 to-yellow-500 text-white"
                              onClick={() => {
                                setShowCreateProjectModal(true);
                              }}
                            >
                              <Plus className="w-4 h-4 mr-1" />
                              Use Template
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>

                {templates.length === 0 && (
                  <Card className="rounded-2xl shadow-sm">
                    <CardContent className="p-12 text-center">
                      <Sparkles className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                      <h3 className="text-xl font-semibold text-gray-900 mb-2">Loading Templates...</h3>
                      <p className="text-gray-500">Pre-built safety workflows will appear here</p>
                    </CardContent>
                  </Card>
                )}
              </div>
            )}
          </>
        )}
      </div>
      
      {/* Modals */}
      <TaskDetailModal
        task={selectedTask}
        isOpen={showTaskModal}
        onClose={() => { setShowTaskModal(false); setSelectedTask(null); }}
        onUpdate={handleUpdateTask}
        onDelete={handleDeleteTask}
        users={users}
        teams={teams}
      />
      
      <CreateTaskModal
        isOpen={showCreateTaskModal}
        onClose={() => setShowCreateTaskModal(false)}
        onCreate={handleCreateTask}
        projects={projects}
        teams={teams}
        templates={templates}
      />
      
      <CreateProjectModal
        isOpen={showCreateProjectModal}
        onClose={() => setShowCreateProjectModal(false)}
        onCreate={handleCreateProject}
        teams={teams}
        templates={templates}
      />
    </div>
  );
};

export default SafetyWorkManagement;
